import {
  handleGenerateBrandUrl,
  handleGenerateProductLinkProps,
} from "@faire/retailer-visitor-shared/components/Carousel/utils";
import {
  BaseCarouselProps,
  CarouselBase,
  IProps as CarouselBaseIProps,
} from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Carousel/CarouselBase";
import { IconTextTile } from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Components/IconTextTile";
import { Context } from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Context";
import { getAltTag } from "@faire/retailer-visitor-shared/features/Marketplace/utils";
import { ILeanProductTile } from "@faire/retailer-visitor-shared/features/MarketplaceItems/MarketplaceProduct/LeanProtos/ILeanProductTile";
import { MarketplaceProduct } from "@faire/retailer-visitor-shared/features/MarketplaceItems/MarketplaceProduct/MarketplaceProduct";
import { ILeanProductWithBrandTile } from "@faire/retailer-visitor-shared/lib/ILeanProductWithBrandTile";
import { getUser } from "@faire/retailer-visitor-shared/serialized-data/getUser";
import { useStrictLocalization } from "@faire/web/common/localization";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { ILayoutElementData } from "@faire/web-api--source/indigofair/data/ILayoutElementData";
import React from "react";
import { useLocation } from "react-router-dom";

export const ProductWithBrandCarousel: React.FC<BaseCarouselProps> = (
  props
) => {
  const location = useLocation();
  const user = getUser();
  const { strictLocalize } = useStrictLocalization();
  const {
    layoutElement,
    requestId,
    onItemClick,
    onCTAClick,
    onVisible,
    pageType,
  } = React.useContext(Context);

  const handleOnProductClick =
    (product: ILeanProductTile.ILeanProduct | undefined) => () => {
      onItemClick?.(
        layoutElement.on_click_event_name ?? "",
        requestId,
        product?.token ?? ""
      );
    };

  const handleOnBrandClick =
    (brand: ILeanProductTile.IBrand | undefined) => () => {
      onItemClick?.(
        layoutElement.on_click_event_name ?? "",
        requestId,
        brand?.token ?? ""
      );
    };

  const renderItem = (data: ILayoutElementData, index: number) => {
    //render icon text tile
    if (data.icon_text_tile) {
      return (
        <IconTextTile
          key={"icon-text-tile-" + index}
          iconTextLayoutElementData={data.icon_text_tile}
          onCTAClick={() => {
            onCTAClick?.(layoutElement.on_click_event_name ?? "", requestId);
          }}
        />
      );
    }

    const tile = ILeanProductWithBrandTile.fromLayoutElementData(
      data,
      strictLocalize
    );

    if (!tile) {
      return null;
    }
    if (tile.product) {
      /**
       * While majority of carousels add the badge_list directly to the product,
       * we are populating the tile_badges_map on the BE for certain carousels. We have to associate
       * the badge with the product here in those cases, otherwise it uses the product
       * badge_list by default.
       */
      const badgeFromMap =
        layoutElement.tile_badges_map?.[tile.product.product?.token ?? ""];
      if (badgeFromMap) {
        tile.product.badge_list = badgeFromMap;
      }
    }

    return (
      <MarketplaceProduct
        key={tile.product?.product?.token}
        productTile={tile.product}
        brand={tile.brand}
        generateProductLinkProps={handleGenerateProductLinkProps(
          tile.product,
          location,
          user,
          props.productPageQueryParams
        )}
        generateBrandUrl={handleGenerateBrandUrl(
          tile.brand?.token,
          tile.product?.product?.token
        )}
        onClick={handleOnProductClick(tile.product?.product)}
        onBrandClick={handleOnBrandClick(tile.brand)}
        showVerticalImagery={layoutElement.show_vertical_imagery}
        altTag={getAltTag(
          {
            productName: tile.product?.product?.name,
            brandName: tile.brand?.name,
            pageType: pageType,
          },
          strictLocalize
        )}
      />
    );
  };

  const getItems = () => {
    return layoutElement.layout_element_data.filter(isNotUndefined);
  };

  const onItemsVisible = (items: ILayoutElementData[]) => {
    const itemTokens = items.map((item) => {
      const tile = ILeanProductWithBrandTile.fromLayoutElementData(
        item,
        strictLocalize
      );
      return tile?.product?.product?.token;
    });
    if (layoutElement.on_click_event_name) {
      onVisible?.(
        layoutElement.on_click_event_name,
        requestId,
        itemTokens?.join(" ") ?? ""
      );
    }
  };

  return (
    <CarouselBaseComponent
      renderItem={renderItem}
      items={getItems()}
      peekNextPercent={{
        mobileAndAbove: 20,
        tabletAndAbove: 0,
      }}
      lateralPadding={16}
      onItemsVisible={onItemsVisible}
      {...props}
    />
  );
};

const CarouselBaseComponent = CarouselBase as React.ComponentType<
  CarouselBaseIProps<ILayoutElementData>
>;
